import React, { useEffect } from 'react';
import { arrayOf, bool, func, number, shape, string } from 'prop-types';
import { Box, Divider, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { useLocation } from 'react-router-dom';

// Components
import DropDownSection from './DropDownSection';
import PillSection from '../../../create-new-common/PillSection';

// Utils
import { ACTIVE_TAB_SELF_AUTH_MAP, STEP_ONE_HEADER } from '../../../utils/const';

import { withVerticalMarginStyles } from '../Steps.styles';

const Details = ({
  authorizationBy,
  activeSelfAuthTab,
  fetchViewByList,
  currentSelection,
  inactiveListLoading,
  params,
  resetEnabledOptions,
  setAuthorizationBy,
  setHasUnsavedChanges,
  setParams,
  viewByListOptions,
}) => {
  const { pathname } = useLocation();
  const { selfAuthType, affiliate, locationId, productId } = params;
  const theme = useTheme();

  useEffect(() => {
    // set selfAuthID with locationID/productID in redux if coming back from Step2
    // or with currentSelection/Product selected in the side panel in Location Management
    // or undefined if neither
    const getSelfAuthId = () => {
      if (authorizationBy === 'location' && locationId) return locationId;
      if (authorizationBy === 'product' && productId) return productId;
      if (pathname.includes('self-authorization') && pathname.includes(authorizationBy))
        return currentSelection.id;
      return null;
    };

    setParams({
      [`${authorizationBy}Id`]: getSelfAuthId(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorizationBy]);

  useEffect(() => {
    setParams({
      selfAuthType: selfAuthType ?? ACTIVE_TAB_SELF_AUTH_MAP[activeSelfAuthTab] ?? 'BASE',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSelfAuthTab]);

  useEffect(() => {
    if (affiliate?.dataCompanyId) {
      fetchViewByList({ affiliate, selfAuthorizationType: selfAuthType }, authorizationBy);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorizationBy, selfAuthType, affiliate?.dataCompanyId]);

  useEffect(() => {
    if (inactiveListLoading) {
      resetEnabledOptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inactiveListLoading, resetEnabledOptions]);

  const handleTypeChange = e => {
    setHasUnsavedChanges(true);
    setAuthorizationBy(e.target.value);
    setParams({
      [`${authorizationBy}Id`]: undefined,
    });
  };

  return (
    <Box display="flex" flexDirection="column" px={3}>
      <Typography color="textPrimary" variant="body1">
        {STEP_ONE_HEADER}
      </Typography>
      <PillSection
        name="selfAuthorizationsType"
        onTypeChange={handleTypeChange}
        typeSelection={authorizationBy}
      />
      <Divider sx={withVerticalMarginStyles(theme)} />
      <DropDownSection
        affiliate={affiliate}
        authorizationBy={authorizationBy}
        locationId={locationId}
        productId={productId}
        selfAuthType={selfAuthType}
        setHasUnsavedChanges={setHasUnsavedChanges}
        setParams={setParams}
        viewByListOptions={viewByListOptions}
      />
    </Box>
  );
};

Details.propTypes = {
  activeSelfAuthTab: string,
  authorizationBy: string.isRequired,
  currentSelection: shape({
    id: number,
    longName: string,
    shortName: string,
  }),
  fetchViewByList: func.isRequired,
  inactiveListLoading: bool.isRequired,
  params: shape({
    affiliate: shape({
      customerIdName: string,
      dataCompanyId: number,
      label: string,
    }),
    locationId: number,
    productId: number,
    selfAuthType: string,
  }),
  resetEnabledOptions: func.isRequired,
  setAuthorizationBy: func.isRequired,
  setHasUnsavedChanges: func.isRequired,
  setParams: func.isRequired,
  viewByListOptions: arrayOf(
    shape({
      id: number,
      longName: string,
      shortName: string,
    })
  ),
};

Details.defaultProps = {
  activeSelfAuthTab: '',
  currentSelection: {},
  params: {},
  viewByListOptions: [],
};

export default Details;
